import React, { useEffect, useState } from "react";
import { getCsrfToken } from "../../../../public/api/authApi";
import { createCallLog, editCallLog, searchUsers } from "../../api/callLogsApi";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import {
  GlobalTextField,
  GlobalContactNumberField,
  GlobalSelectField,
  GlobalDateField,
  GlobalTextAreaField,
  GlobalButton,
} from "../../../../global/forms/GlobalFormFields";
import { toCallLogCalendarDateKey } from "../../utils/callLogDates";
import { masterSelectOptions } from "../../../memberships/utils/masterLookupUtils";

const INITIAL_FORM = {
  call_date: "",
  call_time: "",
  caller_name: "",
  contact_number: "",
  call_type: "",
  purpose_id: "",
  is_call_completed: "",
  handled_by: "",
  call_duration: "",
  action_taken: "",
  follow_up_date: "",
  remarks: "",
};

const getLocalDateString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(now.getDate()).padStart(2, "0")}`;
};

const sanitizeCallerName = (value) => String(value || "").replace(/[^a-zA-Z\s]/g, "");

const CreateCallLogForm = ({
  onCancel,
  onSuccess,
  masterData,
  mode = "create",
  initialValues = null,
}) => {
  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // User Search State
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [showUserResults, setShowUserResults] = useState(false);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState("");
  const isEditMode = mode === "edit" && !!initialValues?.id;

  const handleFieldChange = (name) => (value) => {
    const nextValue = name === "caller_name" ? sanitizeCallerName(value) : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleCallStatusChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      is_call_completed: value,
      follow_up_date: value === "true" ? "" : prev.follow_up_date,
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.is_call_completed;
      if (value === "true") delete next.follow_up_date;
      return next;
    });
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (userSearch.length >= 2) {
        setSearchingUsers(true);
        try {
          const resp = await searchUsers(userSearch);
          setUserResults(resp?.data || []);
          setShowUserResults(true);
        } catch (err) {
          console.error("User search failed", err);
        } finally {
          setSearchingUsers(false);
        }
      } else {
        setUserResults([]);
        setShowUserResults(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [userSearch]);

  useEffect(() => {
    if (!isEditMode) {
      resetForm();
      return;
    }

    setFormData({
      call_date: toCallLogCalendarDateKey(initialValues.call_date),
      call_time: initialValues.call_time || "",
      caller_name: initialValues.caller_name || "",
      contact_number: initialValues.contact_number || "",
      call_type:
        initialValues.call_type !== undefined && initialValues.call_type !== null
          ? String(initialValues.call_type)
          : "",
      purpose_id: initialValues.purpose_id || "",
      is_call_completed:
        initialValues.is_call_completed === 1 || initialValues.is_call_completed === true
          ? "true"
          : "false",
      handled_by: initialValues.handled_by || "",
      call_duration: initialValues.call_duration ?? "",
      action_taken: initialValues.action_taken || "",
      follow_up_date: toCallLogCalendarDateKey(initialValues.follow_up_date),
      remarks: initialValues.remarks || "",
    });

    setSelectedUserName(
      String(initialValues.handled_by_name || initialValues.handled_by || "").trim()
    );
    setUserSearch("");
    setErrors({});
  }, [isEditMode, initialValues]);

  const handleUserSelect = (user) => {
    setFormData((prev) => ({ ...prev, handled_by: user.id }));
    setSelectedUserName(user.name);
    setShowUserResults(false);
    setUserSearch("");
    setErrors((prev) => {
      const next = { ...prev };
      delete next.handled_by;
      return next;
    });
  };

  const handleClearUserSelect = () => {
    setFormData((prev) => ({ ...prev, handled_by: "" }));
    setSelectedUserName("");
    setUserSearch("");
  };

  const resetForm = () => {
    setFormData({
      ...INITIAL_FORM,
      action_taken: "",
      follow_up_date: "",
    });
    setErrors({});
    setSelectedUserName("");
    setUserSearch("");
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.call_date) newErrors.call_date = "Call date is required";
    if (!formData.call_time) newErrors.call_time = "Call time is required";
    if (!formData.caller_name) newErrors.caller_name = "Caller name is required";
    if (formData.call_type === "") newErrors.call_type = "Call type is required";
    if (!formData.purpose_id) newErrors.purpose_id = "Purpose is required";
    if (formData.is_call_completed !== "true" && formData.is_call_completed !== "false") {
      newErrors.is_call_completed = "Select call status";
    }
    if (!formData.handled_by) newErrors.handled_by = "Handled by is required - search and select a user";

    if (!formData.contact_number) {
      newErrors.contact_number = "Contact number is required";
    } else if (!/^[0-9]{10}$/.test(formData.contact_number)) {
      newErrors.contact_number = "Contact number must be exactly 10 digits";
    }

    if (formData.call_duration !== "" && formData.call_duration != null) {
      const d = Number(formData.call_duration);
      if (!Number.isFinite(d) || d < 1) {
        newErrors.call_duration = "Call duration must be at least 1 minute";
      } else if (d > 300) {
        newErrors.call_duration = "Call duration cannot exceed 300 minutes (5 hours)";
      }
    }

    if (formData.call_date && formData.call_time) {
      const now = new Date();
      const localToday = `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

      if (formData.call_date === localToday) {
        const [hh = "0", mm = "0"] = String(formData.call_time).split(":");
        const callMinutes = Number(hh) * 60 + Number(mm);
        const nowMinutes = now.getHours() * 60 + now.getMinutes();
        if (callMinutes > nowMinutes) {
          newErrors.call_time = "Call time cannot be in the future";
        }
      }
    }

    if (formData.is_call_completed === "false" && !formData.follow_up_date) {
      newErrors.follow_up_date = "Follow-up date is required when status is needs follow-up";
    } else if (formData.is_call_completed === "false" && formData.call_date && formData.follow_up_date < formData.call_date) {
      newErrors.follow_up_date = "Follow-up date cannot be before call date";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      const payload = {
        ...formData,
        action_taken: formData.action_taken || null,
        is_call_completed: formData.is_call_completed === "true",
        follow_up_date:
          formData.is_call_completed === "true" ? null : formData.follow_up_date || null,
      };

      if (isEditMode) {
        await editCallLog(initialValues.id, payload, csrfToken);
        onSuccess?.("Call log updated successfully");
      } else {
        const result = await createCallLog(payload, csrfToken);
        if (result?.error) {
          setErrors({
            api: result.error.message || "Failed to save call log",
          });
          return;
        }
        resetForm();
        onSuccess?.("Call log recorded successfully");
      }
    } catch (err) {
      setErrors({ api: err.message || "Failed to save call log" });
    } finally {
      setLoading(false);
    }
  };

  const actions = (
    <>
      <GlobalButton type="submit" variant="primary" disabled={loading}>
        {loading ? "Saving..." : isEditMode ? "Update Call Log" : "Save Call Log"}
      </GlobalButton>
      <GlobalButton type="button" variant="ghost" onClick={resetForm} disabled={loading}>
        Clear Form
      </GlobalButton>
      {onCancel && (
        <GlobalButton variant="ghost" onClick={onCancel} disabled={loading}>
          {isEditMode ? "Cancel Edit" : "Cancel"}
        </GlobalButton>
      )}
    </>
  );

  return (
    <form onSubmit={handleSubmit} noValidate>
      <GlobalFormShell
        legend={isEditMode ? "Edit Call Log" : "Capture Call Log"}
        showAlert={Object.keys(errors).length > 0 || !!errors.api}
        alertText={errors.api || "Please fix the fields below."}
        overflowVisible
        gridClassName="gfs-form-grid--register"
        actions={actions}
      >
        {/* Call Details */}
        <GlobalDateField
          label="Call Date *"
          value={formData.call_date}
          onChange={handleFieldChange("call_date")}
          error={errors.call_date}
          max={getLocalDateString()}
        />
        <GlobalTextField
          label="Call Time *"
          type="time"
          value={formData.call_time}
          onChange={handleFieldChange("call_time")}
          error={errors.call_time}
        />
        <GlobalTextField
          label="Caller Name *"
          value={formData.caller_name}
          onChange={handleFieldChange("caller_name")}
          placeholder="Enter caller name"
          error={errors.caller_name}
        />
        <GlobalContactNumberField
          label="Contact Number *"
          value={formData.contact_number}
          onChange={handleFieldChange("contact_number")}
          placeholder="10-digit phone number"
          error={errors.contact_number}
        />
        <GlobalSelectField
          label="Call Type *"
          value={formData.call_type}
          onChange={handleFieldChange("call_type")}
          options={masterData?.types?.map((t) => ({
            value: String(t.id),
            label: t.type_name,
          }))}
          error={errors.call_type}
        />
        <GlobalSelectField
          label="Call Purpose *"
          value={formData.purpose_id}
          onChange={handleFieldChange("purpose_id")}
          options={masterSelectOptions(masterData?.purposes || [], {
            labelKey: "purpose_name",
            selectedValue: formData.purpose_id,
          })}
          error={errors.purpose_id}
        />

        {/* Handling & Follow-up */}
        <GlobalSelectField
          label="Call status *"
          value={formData.is_call_completed}
          onChange={handleCallStatusChange}
          options={[
            { value: "true", label: "Completed" },
            { value: "false", label: "Needs follow-up" },
          ]}
          placeholderOption="Select status"
          error={errors.is_call_completed}
        />

        {/* User Search for Handled By */}
        <div className="gus-wrap gus-wrap--form-span" style={{ position: "relative" }}>
          <label className="gff-label">Handled By <span style={{ color: "#dc2626" }}>*</span></label>

          {formData.handled_by ? (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0.75rem 1rem",
              background: "var(--accent-soft, #f0f9ff)",
              border: "1px solid var(--accent-muted, #bae6fd)",
              borderRadius: "0.5rem",
              marginTop: "0.5rem"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                <div style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  background: "var(--accent, #0ea5e9)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: "0.75rem",
                  fontWeight: 700
                }}>
                  {selectedUserName.charAt(0).toUpperCase()}
                </div>
                <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1e293b" }}>{selectedUserName}</div>
              </div>
              <GlobalButton variant="ghost" size="sm" onClick={handleClearUserSelect}>
                Change
              </GlobalButton>
            </div>
          ) : (
            <>
              <GlobalTextField
                placeholder="Search name..."
                value={userSearch}
                onChange={setUserSearch}
                error={errors.handled_by}
              />
              {searchingUsers && (
                <div style={{
                  position: "absolute",
                  right: "1rem",
                  top: "2.75rem",
                  fontSize: "0.75rem",
                  color: "var(--accent, #0ea5e9)"
                }}>
                  Searching...
                </div>
              )}
              {showUserResults && userResults.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    backgroundColor: "#fff",
                    border: "1px solid var(--border, #e2e8f0)",
                    width: "100%",
                    zIndex: 10,
                    maxHeight: "200px",
                    overflowY: "auto",
                    borderRadius: "0.5rem",
                    boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                    marginTop: "0.25rem",
                  }}
                >
                  {userResults.map((u) => (
                    <div
                      key={u.id}
                      onClick={() => handleUserSelect(u)}
                      style={{
                        padding: "0.75rem 1rem",
                        cursor: "pointer",
                        borderBottom: "1px solid var(--border, #f1f5f9)",
                        fontSize: "0.875rem",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                    >
                      <div>
                        <div style={{ fontWeight: 600, color: "#1e293b" }}>{u.name}</div>
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "var(--accent, #0ea5e9)", fontWeight: 600 }}>Select</div>
                    </div>
                  ))}
                </div>
              )}
              {showUserResults && userResults.length === 0 && !searchingUsers && userSearch.length >= 2 && (
                <div style={{
                  position: "absolute",
                  backgroundColor: "#fff",
                  border: "1px solid var(--border, #e2e8f0)",
                  width: "100%",
                  zIndex: 10,
                  padding: "0.75rem 1rem",
                  borderRadius: "0.5rem",
                  marginTop: "0.25rem",
                  fontSize: "0.875rem",
                  color: "#64748b"
                }}>
                  No users found
                </div>
              )}
            </>
          )}
        </div>

        <GlobalTextField
          label="Call Duration (min)"
          type="number"
          value={formData.call_duration}
          onChange={handleFieldChange("call_duration")}
          placeholder="1–300 minutes"
          min={1}
          max={300}
          error={errors.call_duration}
        />
        <GlobalDateField
          label={`Follow-up date${formData.is_call_completed === "false" ? " *" : ""}`}
          value={formData.follow_up_date}
          onChange={handleFieldChange("follow_up_date")}
          disabled={formData.is_call_completed !== "false"}
          min={formData.call_date || undefined}
          error={errors.follow_up_date}
        />
        <GlobalTextAreaField
          label="Action Taken"
          value={formData.action_taken}
          onChange={handleFieldChange("action_taken")}
          rows={3}
          placeholder="Enter action taken"
          classNames={{ fieldWrap: "gff-field gfs-remarks-full" }}
        />
        <GlobalTextAreaField
          label="Remarks"
          value={formData.remarks}
          onChange={handleFieldChange("remarks")}
          rows={3}
          classNames={{ fieldWrap: "gff-field gfs-remarks-full" }}
        />
      </GlobalFormShell>
    </form>
  );
};

export default CreateCallLogForm;
